import React, { useState, useEffect } from "react";
import { makeStyles, useTheme } from "@material-ui/styles";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useRouter, isStaffUser, isProd } from "utils";
import PageHeaderTabs from "../../health-safety-forms/components/page-header-tabs";
import { Grid, Card } from "@material-ui/core";
import { SearchDropdown } from "frame/components";
import {
	FormActions,
	FormButton,
	FormSubmitButton,
	Page,
	PageContent,
	ScrollToTopOnMount,
	PopupSnackbar
} from "frame/components";
import { TimesheetForm } from "../../timesheet/components/timesheet-form";
import ReactGA from "react-ga";
import { connect } from "react-redux";
import {
	LIST_LEAVES_ROUTE,
	ADD_LEAVE_ROUTE,
	ADD_ADMIN_LEAVE_ROUTE,
	NEW_LEAVE_PATH
} from "../leave-routes";
import { TASK_ROUTE } from "features/health-safety-forms";
import moment from "moment";
import { useSelector } from "react-redux";
import { onAddTimesheetValueChange, onAddTimesheetReset, onAddTimesheetSave } from "../../timesheet/timesheet-actions";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { isNotNullOrEmpty } from "utils";
import { onList as onListLeaveTypes } from "../../leave-type/actions";

const useStyles = makeStyles((theme) => ({
	body: {
		marginTop: theme.spacing(-2),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	actions: {
		marginTop: theme.spacing(3),
		paddingBottom: theme.spacing(8)
	},
	menu: {
		"padding": theme.spacing(3, 0, 0, 0),
		"& > *": {
			marginRight: theme.spacing(3)
		}
	},
	siteCardRoot: {
		margin: theme.spacing(2, 0),
		padding: theme.spacing(2, 3)
	},
	siteCardFormGroup: {
		margin: theme.spacing(0),
		padding: theme.spacing(1, 0)
	}
}));

const mapStateToProps = ({ timesheetFormReducer, profile, organisations }) => ({
	values: timesheetFormReducer,
	isValid: timesheetFormReducer.isValid,
	role: profile.role,
	currentOrganisationGUID: organisations.currentOrganisation,
	currentStaffId: profile.staffId
});

const mapDispatchToProps = {
	onChange: onAddTimesheetValueChange,
	onReset: onAddTimesheetReset,
	onSave: onAddTimesheetSave,
	onListLeaveTypes
};

const NewLeaveForm = connect(
	mapStateToProps,
	mapDispatchToProps
)(
	({
		values,
		onChange,
		onReset,
		onSave,
		isValid,
		role,
		currentOrganisationGUID,
		currentStaffId,
		onListLeaveTypes
	}) => {
		const classes = useStyles();
		const { history } = useRouter();
		const { match } = useRouter();
		const { siteId } = match.params;
		const theme = useTheme();
		
		useEffect(() => {
			onReset({
				timesheet: {
					staffId: currentStaffId || "",
					startDate: moment().format("YYYY-MM-DD"),
					endDate: moment().format("YYYY-MM-DD"),
					notes: "",
					toggle: false,
					startLatitude: null,
					startLongitude: null,
					endLatitude: null,
					endLongitude: null
				}
			});
		}, []);

		const onSubmit = (event) => {
			event.preventDefault();
			isProd &&
				ReactGA.event({
					category: "Leave",
					action: "Leave Saved",
					label: "New Leave"
				});
		};

		const defaultLeaveValues = {
			siteId: "",
			timesheet: {
				staffId: currentStaffId || "",
				startDate: moment().format("YYYY-MM-DD"),
				endDate: moment().format("YYYY-MM-DD"),
				notes: "",
				toggle: false,
				startLatitude: null,
				startLongitude: null,
				endLatitude: null,
				endLongitude: null
			},
			isEndDateTimeInvalid: false
		};
		
		const [leaveFormValues, setLeaveFormValues] = useState(defaultLeaveValues);
		const leaveTypeList = useSelector(({ leaveTypes }) => leaveTypes.data || []);
		const siteOptions = isNotNullOrEmpty(leaveTypeList)
			? leaveTypeList
					.filter((lt) => lt.active)
					.map((lt) => ({
						label: lt.leaveType,
						value: lt.id
					}))
					.sort((a, b) => (a.label > b.label ? 1 : -1))
			: [];

		useEffect(() => {
			if (siteId) {
				setLeaveFormValues({
					...leaveFormValues,
					siteId: siteId
				});
			}
		}, [siteId]);

		useEffect(() => {
			onListLeaveTypes();
		}, []);

		return (
			<Page title="+ Leave">
				<ScrollToTopOnMount />
				<PageHeaderTabs
					title="+ Leave"
					staffUserHide={false}
					addTabTitle="New Leave"
					hideAddTabIcon={false}
					hideAddTabButton={true}
					addTabPath={NEW_LEAVE_PATH}
					listTabPath={LIST_LEAVES_ROUTE}
					showAddAdminButton={false}
					showLeaveButton={false}
					newLeavePath={NEW_LEAVE_PATH}
					backActionPath={null}
				/>
				<PageContent padSides padBottom>
					<ValidatorForm autoComplete="off" noValidate onSubmit={onSubmit}>
						<Grid container spacing={3}>
							<Grid item lg={6} md={12} sm={12} xs={12}>
								<div className={classes.body}>
									<Card className={classes.siteCardRoot}>
										<div className={classes.siteCardFormGroup}>
											{siteOptions.length == 0 ? (
												<p>Loading Leave Types ...</p>
											) : (
												<SearchDropdown
													id="site"
													data-cy="site"
													label="Leave Type *"
													disabled={false}
													defaultOptions={siteOptions}
													value={
														siteOptions.length > 0 && leaveFormValues.siteId
															? leaveFormValues.siteId
															: ""
													}
													onChange={(e) => {
														setLeaveFormValues({ ...leaveFormValues, siteId: e });
													}}
												/>
											)}
										</div>
									</Card>

									{leaveFormValues.siteId && (
										<TimesheetForm
											values={leaveFormValues}
											onChange={(e) => {
												setLeaveFormValues(e);
											}}
											leaveForm={true}
											showStaffDropdown={!isStaffUser(role)}
										/>
									)}
								</div>
							</Grid>
						</Grid>

						{leaveFormValues.siteId && (
							<FormActions className={classes.actions}>
								<FormSubmitButton
									onClick={() => {
										safetyWiseApi
											.post(`${currentOrganisationGUID}/timesheet/createLeaves`, {
												startDate: leaveFormValues.timesheet.startDate,
												endDate: leaveFormValues.timesheet.endDate,
												staffId: leaveFormValues.timesheet.staffId || currentStaffId,
												leaveTypeID: leaveFormValues.siteId,
												notes: leaveFormValues.timesheet.notes,
												startLatitude: leaveFormValues.timesheet.startLatitude,
												startLongitude: leaveFormValues.timesheet.startLongitude
											})
											.then((result) => {
												console.log(result);
												if (result.data) {
													alert(result.data);
												} else {
													history.push(LIST_LEAVES_ROUTE);
												}
											});
									}}
									disabled={
										!(
											leaveFormValues.timesheet.startDate &&
											leaveFormValues.timesheet.endDate &&
											(isStaffUser(role) ? true : leaveFormValues.timesheet.staffId) &&
											leaveFormValues.siteId
										)
									}
								>
									Save
								</FormSubmitButton>
							<FormButton onClick={() => { history.push(LIST_LEAVES_ROUTE); }}>
								Cancel
							</FormButton>
							</FormActions>
						)}
					</ValidatorForm>
					<PopupSnackbar message={values.errorMessage} coloured error onExited={() => {}} />
				</PageContent>
			</Page>
		);
	}
);

export default NewLeaveForm; 